import type { GridQuery } from '@core/api/types';

/**
 * Centralised query-key factory.
 *
 * With ~45 dropdown endpoints whose keys embed both a search term and parent
 * cascade params, a hand-written key is one typo away from a cache collision that
 * serves the wrong country's regions. Every key in the app comes from here, and
 * `@tanstack/eslint-plugin-query` catches missing dependencies statically.
 */
export const qk = {
  permissions: () => ['permissions'] as const,

  userInfo: () => ['user', 'info'] as const,
  /**
   * Every users query, for invalidation. ⚠️ A user change must refresh ALL of them: invalidating only
   * `allUsers` left Manage Users (keyed `filteredUsers`) showing the old row until a hard refresh.
   */
  users: () => ['users'] as const,
  allUsers: () => ['users', 'all'] as const,
  /** Manage Users' list — search text and role filter are part of the request. */
  filteredUsers: (search: string, roleName: string) =>
    ['users', 'filtered', search, roleName] as const,
  /** Users holding one role — `/management/usersinfo?role_name=`. Keyed so each role caches apart. */
  usersByRole: (roleName: string) => ['users', 'by-role', roleName] as const,
  /** A user's explicit company mappings — under `users`, so `qk.users()` refreshes it too. */
  userCompanyIds: (id: number | string) => ['users', 'company-ids', String(id)] as const,
  userById: (id: number | string) => ['users', String(id)] as const,

  /** `extra` carries cascade parents (country_code, company_code, …). */
  dropdown: (name: string, search: string, extra?: Record<string, unknown> | null) =>
    ['dropdown', name, search, extra ?? null] as const,

  vendor: (id: string) => ['vendor', id] as const,
  vendorHistory: (id: string) => ['vendor', id, 'history'] as const,
  vendorAudit: (id: string) => ['vendor', id, 'audit'] as const,
  /**
   * DISTINCT from `vendorAudit`, which keys the prose narrative
   * (`/audit/vendor/:id/narrative`). This one keys the structured workflow timeline
   * (`/audit/vendor/:id`). Two endpoints, two caches — sharing the key would serve one
   * shape where the other was expected.
   *
   * `workflowType` is part of the key because it is a REQUEST PARAMETER that changes the response:
   * the same partner returns all its workflows unfiltered and one when filtered. Leaving it out
   * would let a filtered response be served for an unfiltered request, or one type's audit be shown
   * for another's.
   */
  vendorAuditTimeline: (id: string, workflowType?: string | null) =>
    ['vendor', id, 'audit', 'timeline', workflowType ?? null] as const,
  vendorExtensionHistory: (
    id: string,
    page: number,
    limit: number,
    extReqId?: string | null,
    status?: string | null,
  ) => ['vendor', id, 'extension-history', page, limit, extReqId ?? null, status ?? null] as const,
  vendorTierChangeHistory: (id: string) => ['vendor', id, 'tier-change-history'] as const,
  vendorDocumentHistory: (id: string, filters: Record<string, unknown>) =>
    ['vendor', id, 'document-history', filters] as const,
  vendorAttributes: (categoryId: string) => ['vendor', 'attributes', categoryId] as const,

  grid: (name: string, query: GridQuery) => ['grid', name, query] as const,

  /**
   * The decision prefilled into the approve/reject dialog.
   *
   * Under `['vendor', id]` on purpose: submitting a verdict can write a new decision, and
   * `invalidateQueries({ queryKey: ['vendor'] })` already runs after every workflow write — so the
   * prefill cannot go stale behind an approver without anyone remembering to invalidate it here.
   *
   * `workflowId` is part of the key because it is a REQUEST PARAMETER that changes the response, for
   * the same reason `vendorAuditTimeline` keys on `workflowType`: one partner can have several live
   * workflows, each with its own decision. Leaving it out would let the extension's decision be
   * served for the creation's verdict dialog.
   */
  recommendationDecision: (vendorId: string, workflowId?: string | null) =>
    ['vendor', vendorId, 'recommendation-decision', workflowId ?? null] as const,

  workflowStats: (range: { start_date: string; end_date: string }) =>
    ['workflow', 'stats', range] as const,
  dashboardCounts: () => ['dashboard', 'counts'] as const,
  doaTurnaround: () => ['dashboard', 'doa-turnaround'] as const,
  riskTiers: () => ['dashboard', 'risk-tiers'] as const,
  workflowStatistics: (instanceId: number | string) =>
    ['workflow', 'statistics', String(instanceId)] as const,
  /** The failed SAP sync behind a request at the SAP level; under `['workflow']` so it refreshes with it. */
  sapRecovery: (instanceId: string) => ['workflow', 'sap-recovery', instanceId] as const,
  workflowDetails: (instanceId: number | string) =>
    ['workflow', 'details', String(instanceId)] as const,
  workflowDefinitions: () => ['workflow', 'definitions'] as const,
  workflowDefinition: (id: number | string) => ['workflow', 'definition', String(id)] as const,
  workflowTypes: () => ['workflow', 'types'] as const,

  /**
   * The DOA approval matrix, per company code AND workflow type.
   *
   * Both are REQUEST PARAMETERS that change the response, so both are in the key — the same reason
   * `vendorAuditTimeline` keys on its workflow type. Keying on the company alone would serve the
   * Creation matrix when the user has switched to Tier Change, which is indistinguishable on screen
   * from the two genuinely being identical (as, today, they are).
   */
  matrixCompanyCodes: () => ['workflow', 'matrix', 'company-codes'] as const,
  matrixWorkflowTypes: () => ['workflow', 'matrix', 'workflow-types'] as const,
  matrixCompany: (companyCode: string, workflowType: string) =>
    ['workflow', 'matrix', companyCode, workflowType] as const,

  /** One company's role -> users mapping. Keyed by code so switching company refetches. */
  companySetup: (companyCode: string) => ['management', 'company-setup', companyCode] as const,

  notifications: () => ['notifications'] as const,

  roles: () => ['roles'] as const,
  roleDetails: (id: number | string) => ['roles', String(id), 'details'] as const,
  roleUsers: (id: number | string) => ['roles', String(id), 'users'] as const,
  actionGroups: () => ['roles', 'action-groups'] as const,

  bulkImports: () => ['bulk', 'imports'] as const,

  /** Customer bulk upload (validate -> preview -> commit). */
  bulkCustomerSettings: () => ['bulk', 'customers', 'settings'] as const,
  bulkCustomerBatches: () => ['bulk', 'customers', 'batches'] as const,
  bulkCustomerBatch: (batchId: string) => ['bulk', 'customers', 'batch', batchId] as const,
  bulkCustomerRows: (
    batchId: string,
    status: string,
    page: number,
    batchStatus: string,
    search = '',
    companyCode = '',
  ) =>
    ['bulk', 'customers', 'rows', batchId, status, page, batchStatus, search, companyCode] as const,

  chatList: () => ['chat', 'list'] as const,
  chat: (chatroomId: number | string) => ['chat', String(chatroomId)] as const,
  favouriteQueries: () => ['favourites'] as const,

  /**
   * Delegation requests, keyed by the status filter.
   *
   * `'delegation'` is the invalidation unit: a verdict moves a row from one status list to another, so
   * both must refetch. Invalidating only the visible key would leave the destination stale.
   */
  /**
   * Delegation lists, keyed by SCOPE and status filter.
   *
   * `scope` separates the three sources — every request, assigned-to-me, and one user's own — which return
   * different sets and must not share a cache entry. `'delegation'` is the invalidation unit: a verdict
   * moves a row between statuses AND between scopes, so all of them refetch together.
   */
  delegationRequests: (scope: string, status: string) =>
    ['delegation', 'requests', scope, status] as const,
  delegationCompanyCodes: (userId: string) => ['delegation', 'company-codes', userId] as const,
} as const;
